/**
 * VOBVOROT PRODUCT PAGE UNIFIED - JavaScript
 * Full functionality: gallery, variants, quantity, add to cart/wishlist
 * Integrates with CartManager and WishlistManager from vobvorot-cart-wishlist-player.js
 */

(function() {
  'use strict';

  console.log('🎮 Product Page Unified JS Loading...');

  // ========================================
  // CART & WISHLIST MANAGERS - OPTIONAL
  // ========================================
  // CartManager and WishlistManager are OPTIONAL dependencies
  // Product page will work without them - they're used only if available
  // This ensures Add to Cart always works even if managers fail to load

  // ========================================
  // PRODUCT GALLERY CLASS
  // ========================================
  class ProductGallery {
    constructor() {
      this.mainImage = document.getElementById('main-product-image');
      this.thumbnails = document.querySelectorAll('.thumbnail-wrapper');
      this.lightbox = document.getElementById('image-lightbox');
      this.lightboxImage = document.getElementById('lightbox-image');
      this.lightboxClose = document.querySelector('.lightbox-close');
      this.prevArrow = document.getElementById('prev-image-btn');
      this.nextArrow = document.getElementById('next-image-btn');
      this.currentImageIndex = 0;

      // Loading animation
      this.imageLoader = document.getElementById('image-loader');
      this.preloadedImages = new Map(); // Cache for preloaded images

      // Thumbnail carousel elements
      this.thumbnailsTrack = document.getElementById('thumbnails-track');
      this.prevThumbnailsBtn = document.getElementById('prev-thumbnails-btn');
      this.nextThumbnailsBtn = document.getElementById('next-thumbnails-btn');
      this.thumbnailScrollIndex = 0;

      // Prevent rapid clicks during image transitions
      this.isTransitioning = false;
      this.transitionTimeout = null;

      // Store bound handlers for cleanup (prevents memory leaks)
      this._boundHandlers = {
        prevArrowClick: null,
        prevArrowTouch: null,
        nextArrowClick: null,
        nextArrowTouch: null,
        prevThumbnailsClick: null,
        nextThumbnailsClick: null,
        mainImageClick: null,
        lightboxCloseClick: null,
        lightboxClick: null,
        keydown: null,
        thumbnailClicks: []
      };

      if (!this.mainImage) {
        console.warn('⚠️ Main product image not found');
        return;
      }

      this.init();
    }

    init() {
      // ФИКС #2: Более надежная инициализация thumbnail handlers
      console.log('🎨 Initializing gallery with', this.thumbnails.length, 'thumbnails');

      this.thumbnails.forEach((thumb, index) => {
        // Проверяем что thumbnail и его img существуют
        const thumbnailImg = thumb.querySelector('.thumbnail-image');
        if (!thumbnailImg) {
          console.warn('⚠️ Thumbnail image not found for index', index);
          return;
        }

        // ФИКС #2: Добавляем event listener с проверками
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.switchImage(thumb, index);
        });

        // Дополнительно: добавляем hover эффект
        thumb.addEventListener('mouseenter', () => {
          thumb.style.borderColor = '#00ff00';
        });
        thumb.addEventListener('mouseleave', () => {
          if (!thumb.classList.contains('active')) {
            thumb.style.borderColor = '#333';
          }
        });

        console.log(`✅ Thumbnail ${index + 1} initialized with URL:`, thumbnailImg.dataset.fullUrl);
      });

      // Main image click opens lightbox
      if (this.mainImage) {
        this.mainImage.addEventListener('click', () => this.openLightbox(this.mainImage.src));
      }

      // Lightbox close handlers
      if (this.lightboxClose) {
        this.lightboxClose.addEventListener('click', () => this.closeLightbox());
      }
      if (this.lightbox) {
        this.lightbox.addEventListener('click', (e) => {
          if (e.target === this.lightbox) {
            this.closeLightbox();
          }
        });
      }

      // Navigation arrows handlers - with touch support for mobile
      if (this.prevArrow) {
        // Create bound handlers for cleanup
        this._boundHandlers.prevArrowClick = (e) => {
          e.preventDefault();
          this.prevImage();
        };
        this._boundHandlers.prevArrowTouch = (e) => {
          e.preventDefault();
          this.prevImage();
        };
        this.prevArrow.addEventListener('click', this._boundHandlers.prevArrowClick);
        this.prevArrow.addEventListener('touchend', this._boundHandlers.prevArrowTouch, { passive: false });
        console.log('✅ Previous arrow initialized (with touch support)');
      }
      if (this.nextArrow) {
        // Create bound handlers for cleanup
        this._boundHandlers.nextArrowClick = (e) => {
          e.preventDefault();
          this.nextImage();
        };
        this._boundHandlers.nextArrowTouch = (e) => {
          e.preventDefault();
          this.nextImage();
        };
        this.nextArrow.addEventListener('click', this._boundHandlers.nextArrowClick);
        this.nextArrow.addEventListener('touchend', this._boundHandlers.nextArrowTouch, { passive: false });
        console.log('✅ Next arrow initialized (with touch support)');
      }

      // Thumbnail carousel navigation
      if (this.prevThumbnailsBtn && this.nextThumbnailsBtn && this.thumbnailsTrack) {
        this.prevThumbnailsBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.scrollThumbnailsPrev();
        });
        this.nextThumbnailsBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.scrollThumbnailsNext();
        });
        console.log('✅ Thumbnail carousel navigation initialized');

        // Initial button state update
        this.updateThumbnailNavButtons();
      }

      // Preload first 3 images for fast initial loading
      this.preloadInitialImages();

      console.log('✅ Product Gallery initialized');
    }

    // Preload first 3 images on page load
    preloadInitialImages() {
      const imagesToPreload = Math.min(3, this.thumbnails.length);

      for (let i = 0; i < imagesToPreload; i++) {
        const thumb = this.thumbnails[i];
        const img = thumb?.querySelector('.thumbnail-image');
        const url = img?.dataset.fullUrl;

        if (url) {
          this.loadImage(url).then(() => {
            console.log(`✅ Preloaded image ${i + 1}/${imagesToPreload}`);
          }).catch(() => {
            console.warn(`⚠️ Failed to preload image ${i + 1}`);
          });
        }
      }

      console.log(`🚀 Started preloading ${imagesToPreload} images`);
    }

    switchImage(thumb, index) {
      // ЗАЩИТА: Блокировать быстрые клики во время переключения
      if (this.isTransitioning) {
        console.log('⏸️ Image transition in progress, please wait...');
        return;
      }

      // Enhanced image switching with preloading and loading animation
      console.log('🖼️ Switching to image', index + 1);

      // Find img inside thumbnail
      const thumbnailImg = thumb.querySelector('.thumbnail-image');
      if (!thumbnailImg) {
        console.error('❌ Thumbnail image element not found');
        return;
      }

      // Get full image URL
      const fullUrl = thumbnailImg.dataset.fullUrl;
      if (!fullUrl) {
        console.error('❌ No full URL found in data-full-url attribute');
        return;
      }

      console.log('📸 Full URL:', fullUrl);

      // Установить флаг переключения
      this.isTransitioning = true;

      // Очистить предыдущий таймаут если есть
      if (this.transitionTimeout) {
        clearTimeout(this.transitionTimeout);
      }

      // Show loading animation
      if (this.imageLoader) {
        this.imageLoader.style.display = 'flex';
      }

      // Remove active from all thumbnails
      this.thumbnails.forEach(t => {
        t.classList.remove('active');
        t.style.borderColor = '#333';
      });

      // Add active to selected thumbnail
      thumb.classList.add('active');
      thumb.style.borderColor = '#00ff00';

      // Update current image index
      this.currentImageIndex = index;

      // Show spinner for minimum 300ms for smooth transition
      const minLoadTime = new Promise(resolve => setTimeout(resolve, 300));

      // Load image with preloading
      Promise.all([this.loadImage(fullUrl), minLoadTime]).then(() => {
        if (this.mainImage) {
          // Fade effect during image switch
          this.mainImage.style.opacity = '0.5';

          this.transitionTimeout = setTimeout(() => {
            this.mainImage.src = fullUrl;
            this.mainImage.style.opacity = '1';

            // Hide loading animation
            if (this.imageLoader) {
              this.imageLoader.style.display = 'none';
            }

            // Снять флаг переключения
            this.isTransitioning = false;

            console.log('✅ Main image updated successfully');

            // Preload adjacent images
            this.preloadAdjacentImages(index);
          }, 150);
        }
      }).catch((error) => {
        console.error('❌ Error loading image:', error);
        if (this.imageLoader) {
          this.imageLoader.style.display = 'none';
        }
        // Снять флаг переключения при ошибке
        this.isTransitioning = false;
      });
    }

    // Load image with promise
    loadImage(url) {
      // Check if already preloaded
      if (this.preloadedImages.has(url)) {
        return Promise.resolve(this.preloadedImages.get(url));
      }

      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          this.preloadedImages.set(url, img);
          resolve(img);
        };
        img.onerror = reject;
        img.src = url;
      });
    }

    // Preload adjacent images (next and previous)
    preloadAdjacentImages(currentIndex) {
      const totalImages = this.thumbnails.length;

      // Preload next image
      const nextIndex = (currentIndex + 1) % totalImages;
      if (nextIndex < totalImages) {
        const nextThumb = this.thumbnails[nextIndex];
        const nextImg = nextThumb?.querySelector('.thumbnail-image');
        const nextUrl = nextImg?.dataset.fullUrl;
        if (nextUrl && !this.preloadedImages.has(nextUrl)) {
          this.loadImage(nextUrl).catch(() => {});
        }
      }

      // Preload previous image
      const prevIndex = (currentIndex - 1 + totalImages) % totalImages;
      if (prevIndex >= 0) {
        const prevThumb = this.thumbnails[prevIndex];
        const prevImg = prevThumb?.querySelector('.thumbnail-image');
        const prevUrl = prevImg?.dataset.fullUrl;
        if (prevUrl && !this.preloadedImages.has(prevUrl)) {
          this.loadImage(prevUrl).catch(() => {});
        }
      }

      console.log(`🔄 Preloaded adjacent images for position ${currentIndex}`);
    }

    nextImage() {
      if (this.thumbnails.length === 0) return;

      // Calculate next index (loop back to 0 if at end)
      const nextIndex = (this.currentImageIndex + 1) % this.thumbnails.length;
      const nextThumb = this.thumbnails[nextIndex];

      console.log('➡️ Next image:', nextIndex + 1);
      this.switchImage(nextThumb, nextIndex);
    }

    prevImage() {
      if (this.thumbnails.length === 0) return;

      // Calculate previous index (loop to end if at start)
      const prevIndex = this.currentImageIndex === 0
        ? this.thumbnails.length - 1
        : this.currentImageIndex - 1;
      const prevThumb = this.thumbnails[prevIndex];

      console.log('⬅️ Previous image:', prevIndex + 1);
      this.switchImage(prevThumb, prevIndex);
    }

    scrollThumbnailsNext() {
      if (!this.thumbnailsTrack) return;

      // Get thumbnail width (80px) + gap (10px) = 90px on desktop
      const thumbnailWidth = window.innerWidth <= 768 ? 70 : 90; // 60px + 10px gap on mobile

      // Calculate max scroll (don't scroll past last thumbnail)
      const trackWidth = this.thumbnailsTrack.scrollWidth;
      const containerWidth = this.thumbnailsTrack.parentElement.offsetWidth;
      const maxScroll = Math.ceil((trackWidth - containerWidth) / thumbnailWidth);

      if (this.thumbnailScrollIndex < maxScroll) {
        this.thumbnailScrollIndex++;
        const translateX = -this.thumbnailScrollIndex * thumbnailWidth;
        this.thumbnailsTrack.style.transform = `translateX(${translateX}px)`;

        console.log('➡️ Scrolled thumbnails to index:', this.thumbnailScrollIndex);
        this.updateThumbnailNavButtons();
      }
    }

    scrollThumbnailsPrev() {
      if (!this.thumbnailsTrack) return;

      const thumbnailWidth = window.innerWidth <= 768 ? 70 : 90;

      if (this.thumbnailScrollIndex > 0) {
        this.thumbnailScrollIndex--;
        const translateX = -this.thumbnailScrollIndex * thumbnailWidth;
        this.thumbnailsTrack.style.transform = `translateX(${translateX}px)`;

        console.log('⬅️ Scrolled thumbnails to index:', this.thumbnailScrollIndex);
        this.updateThumbnailNavButtons();
      }
    }

    updateThumbnailNavButtons() {
      if (!this.prevThumbnailsBtn || !this.nextThumbnailsBtn || !this.thumbnailsTrack) return;

      const thumbnailWidth = window.innerWidth <= 768 ? 70 : 90;
      const trackWidth = this.thumbnailsTrack.scrollWidth;
      const containerWidth = this.thumbnailsTrack.parentElement.offsetWidth;
      const maxScroll = Math.ceil((trackWidth - containerWidth) / thumbnailWidth);

      // Disable prev button at start
      this.prevThumbnailsBtn.disabled = this.thumbnailScrollIndex === 0;

      // Disable next button at end
      this.nextThumbnailsBtn.disabled = this.thumbnailScrollIndex >= maxScroll;

      console.log('🎚️ Thumbnail nav buttons updated - Scroll:', this.thumbnailScrollIndex, 'Max:', maxScroll);
    }

    openLightbox(imageSrc) {
      if (this.lightbox && this.lightboxImage) {
        this.lightboxImage.src = imageSrc;
        this.lightbox.style.display = 'block';
        console.log('🔍 Lightbox opened');
      }
    }

    closeLightbox() {
      if (this.lightbox) {
        this.lightbox.style.display = 'none';
        console.log('❌ Lightbox closed');
      }
    }

    // Cleanup method to prevent memory leaks during SPA navigation
    destroy() {
      console.log('🧹 Cleaning up ProductGallery event listeners...');

      // Remove arrow handlers
      if (this.prevArrow) {
        if (this._boundHandlers.prevArrowClick) {
          this.prevArrow.removeEventListener('click', this._boundHandlers.prevArrowClick);
        }
        if (this._boundHandlers.prevArrowTouch) {
          this.prevArrow.removeEventListener('touchend', this._boundHandlers.prevArrowTouch);
        }
      }
      if (this.nextArrow) {
        if (this._boundHandlers.nextArrowClick) {
          this.nextArrow.removeEventListener('click', this._boundHandlers.nextArrowClick);
        }
        if (this._boundHandlers.nextArrowTouch) {
          this.nextArrow.removeEventListener('touchend', this._boundHandlers.nextArrowTouch);
        }
      }

      // Remove thumbnail carousel handlers
      if (this.prevThumbnailsBtn && this._boundHandlers.prevThumbnailsClick) {
        this.prevThumbnailsBtn.removeEventListener('click', this._boundHandlers.prevThumbnailsClick);
      }
      if (this.nextThumbnailsBtn && this._boundHandlers.nextThumbnailsClick) {
        this.nextThumbnailsBtn.removeEventListener('click', this._boundHandlers.nextThumbnailsClick);
      }

      // Remove keydown handler
      if (this._boundHandlers.keydown) {
        document.removeEventListener('keydown', this._boundHandlers.keydown);
      }

      // Clear preloaded images cache
      this.preloadedImages.clear();

      // Clear transition timeout
      if (this.transitionTimeout) {
        clearTimeout(this.transitionTimeout);
      }

      console.log('✅ ProductGallery cleanup complete');
    }
  }

  // ========================================
  // VARIANT SELECTOR CLASS
  // ========================================
  class VariantSelector {
    constructor(product) {
      this.product = product;
      this.form = document.getElementById('product-variant-form');
      this.variantButtons = document.querySelectorAll('.variant-btn');
      this.variantIdInput = document.getElementById('product-variant-id');
      this.selectedOptions = {};

      // 🔍 ДИАГНОСТИКА PROBLEM #1: Логирование инициализации
      console.log('═══ VARIANT SELECTOR INIT ═══');
      console.log('🔧 Form found:', !!this.form);
      console.log('🔧 Variant buttons:', this.variantButtons.length);
      console.log('🔧 Product has only default variant:', product.has_only_default_variant);

      if (!this.form) {
        console.log('ℹ️ No variant form found (product has only default variant)');

        // Даже для default variant нужно проверить quantity selector
        if (product.variants && product.variants.length > 0) {
          const defaultVariant = product.variants[0];
          console.log('🔧 Updating quantity selector for default variant');
          this.updateQuantitySelector(defaultVariant);
        }
        console.log('═══════════════════════════');
        return;
      }

      console.log('═══════════════════════════');
      this.init();
    }

    init() {
      // Initialize selected options from active buttons
      this.variantButtons.forEach(btn => {
        const optionIndex = btn.dataset.optionIndex;
        if (btn.classList.contains('active')) {
          this.selectedOptions[optionIndex] = btn.dataset.value;
        }

        // Add click handlers
        btn.addEventListener('click', (e) => this.handleVariantClick(e));
      });

      console.log('✅ Variant Selector initialized with options:', this.selectedOptions);
    }

    handleVariantClick(e) {
      const button = e.currentTarget;
      const optionIndex = button.dataset.optionIndex;
      const value = button.dataset.value;

      // Remove active from siblings
      const siblings = document.querySelectorAll(`[data-option-index="${optionIndex}"]`);
      siblings.forEach(btn => btn.classList.remove('active'));

      // Add active to clicked button
      button.classList.add('active');

      // Update selected options
      this.selectedOptions[optionIndex] = value;

      // Find matching variant
      this.updateVariant();
    }

    updateVariant() {
      // Convert selected options to array matching product.variants format
      const selectedValues = Object.values(this.selectedOptions);

      // Find variant matching all selected options
      const matchingVariant = this.product.variants.find(variant => {
        return selectedValues.every((value, index) => {
          return variant.options[index] === value;
        });
      });

      if (matchingVariant) {
        console.log('✅ Found matching variant:', matchingVariant.id);

        // Update hidden input
        if (this.variantIdInput) {
          this.variantIdInput.value = matchingVariant.id;
        }

        // Update price
        this.updatePrice(matchingVariant);

        // Update stock
        this.updateStock(matchingVariant);

        // Update quantity selector visibility
        this.updateQuantitySelector(matchingVariant);

        // Update add to cart button
        this.updateAddToCartButton(matchingVariant);

        // Dispatch custom event for other components
        document.dispatchEvent(new CustomEvent('variantChanged', {
          detail: { variant: matchingVariant }
        }));
      } else {
        console.warn('⚠️ No matching variant found for:', selectedValues);
      }
    }

    updatePrice(variant) {
      const priceElement = document.getElementById('product-price');
      if (priceElement) {
        priceElement.textContent = this.formatMoney(variant.price);
      }
    }

    updateStock(variant) {
      const stockElement = document.getElementById('stock-indicator');
      if (!stockElement) return;

      // ФИКС #6: Упрощенная логика - не показываем количество при смене варианта
      if (variant.available) {
        stockElement.innerHTML = '<span class="stock-available">IN STOCK</span>';
      } else {
        stockElement.innerHTML = '<span class="stock-unavailable">OUT OF STOCK</span>';
      }
    }

    updateQuantitySelector(variant) {
      const quantityContainer = document.getElementById('quantity-selector-container');
      if (!quantityContainer) {
        console.log('⚠️ Quantity container not found - this is normal for server-side rendering');
        return;
      }

      // 🔍 ДИАГНОСТИКА PROBLEM #1: Подробное логирование quantity selector
      console.log('═══ QUANTITY SELECTOR UPDATE ═══');
      console.log('🔍 Variant ID:', variant.id);
      console.log('🔍 Variant title:', variant.title);
      console.log('🔍 Inventory quantity:', variant.inventory_quantity);
      console.log('🔍 Checking condition: inventory_quantity >= 2');
      console.log('🔍 Condition result:', variant.inventory_quantity >= 2);

      // Show/hide quantity selector based on inventory
      if (variant.inventory_quantity >= 2) {
        quantityContainer.style.display = 'flex';
        console.log('✅ SHOWING quantity selector (inventory >= 2)');

        // Update max quantity
        const quantityInput = document.getElementById('product-quantity');
        if (quantityInput) {
          quantityInput.max = variant.inventory_quantity;
          console.log('✅ Max quantity set to:', variant.inventory_quantity);
          // Reset to 1 if current value exceeds max
          if (parseInt(quantityInput.value) > variant.inventory_quantity) {
            quantityInput.value = 1;
            console.log('✅ Quantity reset to 1 (exceeded max)');
          }
        }
      } else {
        quantityContainer.style.display = 'none';
        console.log('❌ HIDING quantity selector (inventory < 2)');
      }
      console.log('═══════════════════════════════');
    }

    updateAddToCartButton(variant) {
      const addToCartBtn = document.getElementById('add-to-cart-btn');
      if (!addToCartBtn) return;

      if (variant.available) {
        addToCartBtn.disabled = false;
        addToCartBtn.dataset.variantId = variant.id;
      } else {
        addToCartBtn.disabled = true;
      }
    }

    formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  // ========================================
  // QUANTITY CONTROLLER CLASS
  // ========================================
  class QuantityController {
    constructor() {
      this.input = document.getElementById('product-quantity');
      this.minusBtn = document.querySelector('.quantity-minus');
      this.plusBtn = document.querySelector('.quantity-plus');

      if (!this.input) {
        console.log('ℹ️ No quantity selector found (inventory < 2)');
        return;
      }

      this.init();
    }

    init() {
      if (this.minusBtn) {
        this.minusBtn.addEventListener('click', () => this.decrease());
      }
      if (this.plusBtn) {
        this.plusBtn.addEventListener('click', () => this.increase());
      }

      console.log('✅ Quantity Controller initialized');
    }

    decrease() {
      const currentValue = parseInt(this.input.value);
      const minValue = parseInt(this.input.min);

      if (currentValue > minValue) {
        this.input.value = currentValue - 1;
        console.log('➖ Quantity decreased to', this.input.value);
      }
    }

    increase() {
      const currentValue = parseInt(this.input.value);
      const maxValue = parseInt(this.input.max);

      if (currentValue < maxValue) {
        this.input.value = currentValue + 1;
        console.log('➕ Quantity increased to', this.input.value);
      } else {
        console.warn('⚠️ Maximum quantity reached:', maxValue);
      }
    }

    getValue() {
      // ФИКС: Безопасный доступ - если input отсутствует (нет quantity selector на странице)
      if (!this.input) {
        return 1;
      }
      return parseInt(this.input.value) || 1;
    }
  }

  // ========================================
  // ADD TO CART HANDLER CLASS
  // ========================================
  // ✨ PROFESSIONAL IMPLEMENTATION USING SHOPIFY BEST PRACTICES
  // - Uses native Shopify Cart API (/cart/add.js) directly
  // - No dependency on external CartManager - works independently
  // - Graceful fallback if modals/managers unavailable
  // - Always reliable regardless of script load order
  class AddToCartHandler {
    constructor(quantityController) {
      this.quantityController = quantityController;
      this.button = document.getElementById('add-to-cart-btn');

      if (!this.button) {
        console.warn('⚠️ Add to cart button not found');
        return;
      }

      this.init();
    }

    init() {
      this.button.addEventListener('click', () => this.handleAddToCart());
      console.log('✅ Add to Cart Handler initialized (Shopify native API)');
    }

    async handleAddToCart() {
      console.log('🛒 ADD TO CART CALLED - Professional Shopify API implementation');

      // Get variant ID from button data attribute
      const variantIdFromDataset = this.button.dataset.variantId;
      const variantIdFromInput = document.getElementById('product-variant-id')?.value;
      const variantId = variantIdFromDataset || variantIdFromInput;

      console.log('🛒 Variant ID from dataset:', variantIdFromDataset);
      console.log('🛒 Variant ID from input:', variantIdFromInput);
      console.log('🛒 Final variant ID:', variantId);

      const quantity = this.quantityController ? this.quantityController.getValue() : 1;
      console.log('🛒 Quantity:', quantity);

      if (!variantId) {
        console.error('❌ No variant ID found!');
        console.error('❌ Button HTML:', this.button.outerHTML);
        alert('Error: Product variant not found. Please refresh the page.');
        return;
      }

      // Disable button during request
      this.button.disabled = true;
      const originalText = this.button.querySelector('.glitch-btn-text').textContent;
      this.button.querySelector('.glitch-btn-text').textContent = 'ADDING...';
      console.log('🛒 Button disabled, calling Shopify Cart API...');

      try {
        // ============================================
        // SHOPIFY BEST PRACTICE: Direct Cart API call
        // No dependency on external managers - always works
        // ============================================
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [{
              id: parseInt(variantId),
              quantity: quantity
            }]
          })
        });

        console.log('📦 Shopify API Response:', response.status, response.statusText);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ Shopify API Error:', response.status, errorText);
          throw new Error(`Failed to add to cart: ${response.status}`);
        }

        const data = await response.json();
        console.log('✅ SUCCESS! Item added to cart:', data);

        this.button.querySelector('.glitch-btn-text').textContent = 'ADDED!';

        // Optional enhancement: Update CartManager if available
        if (window.cartManager) {
          console.log('🔄 Refreshing CartManager...');
          await window.cartManager.refreshCart();
        } else {
          console.log('ℹ️ CartManager not available - skipping refresh (cart still updated)');
        }

        // Optional enhancement: Open modal if available
        if (window.modalController) {
          console.log('✅ Opening cart modal...');
          setTimeout(() => {
            window.modalController.openCartModal();
          }, 500);
        } else {
          console.log('ℹ️ Modal not available - showing success message');
          setTimeout(() => {
            if (confirm('✅ Product added to cart! Go to cart?')) {
              window.location.href = '/cart';
            }
          }, 300);
        }

        // Reset button after 2 seconds
        setTimeout(() => {
          this.button.querySelector('.glitch-btn-text').textContent = originalText;
          this.button.disabled = false;
        }, 2000);

      } catch (error) {
        console.error('❌ Add to cart error:', error);
        console.error('❌ Error details:', error.message);
        console.error('❌ Error stack:', error.stack);
        this.button.querySelector('.glitch-btn-text').textContent = 'ERROR';
        alert('Failed to add to cart. Please try again or contact support.');

        setTimeout(() => {
          this.button.querySelector('.glitch-btn-text').textContent = originalText;
          this.button.disabled = false;
        }, 2000);
      }
    }
  }

  // ========================================
  // ADD TO WISHLIST HANDLER CLASS
  // ========================================
  class AddToWishlistHandler {
    constructor() {
      this.button = document.getElementById('add-to-wishlist-btn');

      if (!this.button) {
        console.warn('⚠️ Add to wishlist button not found');
        return;
      }

      this.init();
    }

    init() {
      this.button.addEventListener('click', () => this.handleAddToWishlist());
      console.log('✅ Add to Wishlist Handler initialized');
    }

    handleAddToWishlist() {
      if (!window.wishlistManager) {
        console.error('❌ WishlistManager not available');
        alert('Wishlist system not ready. Please refresh the page.');
        return;
      }

      // ФИКС #3: Формируем правильную структуру данных с фото
      const productData = {
        id: this.button.dataset.productId,
        title: this.button.dataset.productTitle,
        featured_image: this.button.dataset.productImage, // ВАЖНО: правильное название поля
        price: parseInt(this.button.dataset.productPrice),
        handle: this.button.dataset.productUrl.split('/').pop(), // Извлекаем handle из URL
        vendor: '' // Vendor можно получить из window.productJSON если нужно
      };

      console.log('💖 Adding to wishlist:', productData);

      const originalText = this.button.querySelector('span').textContent;

      const success = window.wishlistManager.addItem(productData);

      if (success) {
        console.log('✅ Added to wishlist successfully');
        this.button.querySelector('span').textContent = 'ADDED TO WISHLIST!';

        // ФИКС #3: Открываем wishlist modal после добавления
        if (window.modalController) {
          setTimeout(() => {
            window.modalController.openWishlistModal();
          }, 500);
        }

        // Reset button after 2 seconds
        setTimeout(() => {
          this.button.querySelector('span').textContent = originalText;
        }, 2000);
      } else {
        console.log('ℹ️ Already in wishlist');
        this.button.querySelector('span').textContent = 'ALREADY IN WISHLIST';

        setTimeout(() => {
          this.button.querySelector('span').textContent = originalText;
        }, 2000);
      }
    }
  }

  // ========================================
  // MAIN INITIALIZATION
  // ========================================

  // Store instances globally for cleanup during SPA navigation
  window._productPageInstances = window._productPageInstances || {
    gallery: null,
    variantSelector: null,
    quantityController: null,
    addToCartHandler: null,
    addToWishlistHandler: null
  };

  function initProductPage() {
    console.log('🎮 Initializing Product Page...');

    // 🧹 CLEANUP: Destroy old instances to prevent memory leaks
    if (window._productPageInstances.gallery) {
      window._productPageInstances.gallery.destroy();
      console.log('🧹 Previous gallery instance destroyed');
    }

    // Get product data from page
    const productData = window.productJSON || null;

    if (!productData) {
      console.error('❌ Product data not found on page');
      return;
    }

    console.log('📦 Product loaded:', productData.title);

    // 🔍 ДИАГНОСТИКА PROBLEM #1: Подробная информация о продукте
    console.log('═══ PRODUCT DIAGNOSTICS ═══');
    console.log('📋 Product ID:', productData.id);
    console.log('📋 Has only default variant:', productData.has_only_default_variant);
    console.log('📋 Total variants:', productData.variants?.length || 0);

    if (productData.variants && productData.variants.length > 0) {
      const firstVariant = productData.variants[0];
      console.log('📋 First variant:');
      console.log('   - ID:', firstVariant.id);
      console.log('   - Title:', firstVariant.title);
      console.log('   - Inventory quantity:', firstVariant.inventory_quantity);
      console.log('   - Available:', firstVariant.available);
    }
    console.log('═══════════════════════════');

    // Initialize all components and store references for cleanup
    window._productPageInstances.gallery = new ProductGallery();
    window._productPageInstances.variantSelector = new VariantSelector(productData);
    window._productPageInstances.quantityController = new QuantityController();
    window._productPageInstances.addToCartHandler = new AddToCartHandler(window._productPageInstances.quantityController);
    window._productPageInstances.addToWishlistHandler = new AddToWishlistHandler();

    console.log('✅ All product page components initialized');
  }

  // Initialize immediately - managers are optional dependencies
  // CartManager and WishlistManager will be used if available, but won't block initialization
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductPage);
  } else {
    initProductPage();
  }

  // Expose initProductPage globally for SPA reinitialization
  window.initProductPage = initProductPage;

})();
